import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Package,
  Calendar,
  ChevronRight,
  Pause,
  X,
  RefreshCw,
  Truck,
  MapPin,
  FileText,
  AlertCircle,
  Pencil,
  Play,
  Ban,
  ChevronUp,
  ChevronDown,
  CreditCard,
  Info,
  Plus,
  Minus,
  ChevronLeft,
} from "lucide-react";
import PauseSubscriptionModal from "../../shared/modal/PauseSubscriptionModal";
import ResumeSubscriptionModal from "../../shared/modal/ResumeSubscriptionModal";
import CancelSubscriptionModal from "../../shared/modal/CancelSubscriptionModal";
import SkipDeliveryModal from "../../shared/modal/SkipDeliveryModal";
import SubscriptionPaymentModal from "../../shared/modal/SubscriptionPaymentModal";
import SubscriptionModal from "../../shared/modal/SubscriptionModal";
import DeliveryMethodFields from "../profile/elements/DeliveryMethodFields";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSubscriptionsDetails,
  subscriptionsCancel,
  subscriptionsPause,
  subscriptionsResume,
  skipDelivery,
  updateSubscriptionDelivery,
  updateSubscriptionItems,
  updateSubscriptionFrequency,
} from "../../store/thunk/ordersThunk";
import noproducts from "../../assets/img/default-product.png";
import {
  getModificationTitle,
} from "../../hooks/commonFunction";

const AdminSubscriptionDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { coreSettings } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeliveryMethodModal, setShowDeliveryMethodModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAllSchedules, setShowAllSchedules] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [editQuantity, setEditQuantity] = useState(1);

  const loadSubscriptionDetails = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const result = await dispatch(
        fetchSubscriptionsDetails(id)
      ).unwrap();
      if (result.status === 1 && result.data) {
        setSubscription(result.data);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error("Failed to fetch subscription details:", error);
      toast.error("Failed to load subscription details");
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptionDetails();
  }, [dispatch, id]);

  const handlePauseClick = () => {
    setShowPauseModal(true);
  };

  const handleResumeClick = () => {
    setShowResumeModal(true);
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handlePaymentClick = () => {
    setShowPaymentModal(true);
  };

  const handleUpdateFrequencyClick = () => {
    setShowFrequencyModal(true);
  };

  const confirmUpdateFrequency = async ({ frequency_id, update_reason }) => {
    setIsSubmitting(true);
    const payload = {
      subscriptionId: subscription?.id,
      frequency_id,
      update_reason,
    };
    try {
      const result = await dispatch(updateSubscriptionFrequency(payload)).unwrap();

      if (result?.status === 1) {
        toast.success(result?.message || "Frequency updated successfully!");
        setShowFrequencyModal(false);
        loadSubscriptionDetails();
      } else {
        toast.error(result?.message || "Failed to update frequency");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update frequency");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmPause = async (pause) => {
    setIsSubmitting(true);
    const payload = {
      subscriptionId: subscription?.id,
      next_delivery_date: pause?.date,
      ...(pause.reason && { reason: pause.reason }),
    };
    try {
      const result = await dispatch(subscriptionsPause(payload)).unwrap();

      if (result?.status === 1) {
        toast.success(result?.message || "Subscription paused successfully!");
        setShowPauseModal(false);
        loadSubscriptionDetails();
      } else {
        toast.error(result?.message || "Failed to pause subscription");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to pause subscription");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmCancel = async (reason) => {
    setIsSubmitting(true);
    const payload = {
      subscriptionId: subscription?.id,
      reason: reason,
    };
    try {
      const result = await dispatch(subscriptionsCancel(payload)).unwrap();

      if (result?.status === 1) {
        toast.success(
          result?.message || "Subscription cancelled successfully!"
        );
        setShowCancelModal(false);
        loadSubscriptionDetails();
      } else {
        toast.error(result?.message || "Failed to cancel subscription");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to cancel subscription");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipClick = (schedule) => {
    setSelectedSchedule(schedule);
    setShowSkipModal(true);
  };

  const confirmSkip = async (reason) => {
    setIsSubmitting(true);
    const payload = {
      subscriptionId: subscription?.id,
      scheduleId: selectedSchedule?.id,
      skip_reason: reason || "",
    };
    try {
      const result = await dispatch(skipDelivery(payload)).unwrap();

      if (result?.status === 1) {
        toast.success(result?.message || "Delivery skipped successfully!");
        setShowSkipModal(false);
        setSelectedSchedule(null);
        loadSubscriptionDetails();
      } else {
        toast.error(result?.message || "Failed to skip delivery");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to skip delivery");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddressSelect = (delivery) => {
    setSelectedAddress(delivery);
  };

  const handleUpdateDeliveryMethod = async () => {
    if (!selectedAddress) {
      toast.error("Please select a delivery location");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        subscriptionId: subscription.id,
        delivery_method: selectedAddress.method,
        pickup_id: selectedAddress.pickup_id,
      };
      const result = await dispatch(
        updateSubscriptionDelivery(payload)
      ).unwrap();

      if (result?.status === 1) {
        toast.success(
          result?.message || "Delivery method updated successfully!"
        );
        setShowDeliveryMethodModal(false);
        setSelectedAddress(null);
        loadSubscriptionDetails();
      } else {
        toast.error(result?.message || "Failed to update delivery method");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update delivery method");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmResume = async (selectedDate) => {
    setIsSubmitting(true);
    const payload = {
      subscriptionId: subscription?.id,
      next_delivery_date: selectedDate,
    };
    try {
      const result = await dispatch(subscriptionsResume(payload)).unwrap();

      if (result?.status === 1) {
        toast.success(result?.message || "Subscription resumed successfully!");
        setShowResumeModal(false);
        loadSubscriptionDetails();
      } else {
        toast.error(result?.message || "Failed to resume subscription");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to resume subscription");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditProductClick = () => {
    setEditQuantity(subscription?.item?.quantity || 1);
    setIsEditingProduct(true);
  };

  const handleCancelEdit = () => {
    setIsEditingProduct(false);
    setEditQuantity(subscription?.item?.quantity || 1);
  };

  const handleUpdateQuantity = async () => {
    if (editQuantity < 1) {
      toast.error("Quantity must be at least 1");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        subscriptionId: subscription.id,
        items: [
          {
            product_id: subscription.item.product_id,
            quantity: editQuantity,
            price: parseFloat(subscription.item.product_price),
          },
        ],
        update_reason: "Updated quantity",
      };
      const result = await dispatch(updateSubscriptionItems(payload)).unwrap();

      if (result?.status === 1) {
        toast.success(result?.message || "Quantity updated successfully!");
        setIsEditingProduct(false);
        loadSubscriptionDetails();
      } else {
        toast.error(result?.message || "Failed to update quantity");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update quantity");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDeliveryModal = () => {
    setShowDeliveryMethodModal(false);
    setSelectedAddress(null);
  };

  return (
    <div>
      <div className="container px-0 lg:px-10 pb-20 lg:pb-10 lg:py-10 md:p-5">
            <div className="md:bg-[#f9f9f9] md:border border-neutral-200 rounded-lg md:shadow-[0_3px_10px_rgb(0,0,0,0.2)]">
              {/* Header */}
              <div className="md:border-b border-gray-200 py-3 px-6 flex items-center justify-between">
                <div className="flex items-center flex-1">
                  <button
                    onClick={() => navigate(-1)}
                    className="md:hidden mr-3 p-1 hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center flex-shrink-0"
                    aria-label="Go back"
                  >
                    <ChevronLeft className="w-6 h-6 text-gray-700" />
                  </button>
                  <h1 className="text-2xl font-bold text-primary-text whitespace-nowrap">
                    Subscription Details
                  </h1>
                  <hr className="w-full h-[1px] bg-neutral-300 ml-3 md:hidden block" />
                </div>

                {subscription?.status === "paused" && (
                  <button
                    onClick={handleResumeClick}
                    disabled={isSubmitting}
                    className="px-3 md:px-6 py-2 border-2 border-green-500 text-green-600 hover:bg-green-50 font-semibold rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm md:text-base whitespace-nowrap ml-4"
                  >
                    <Play size={16} />
                    <span className="hidden md:inline">
                      Resume Subscription
                    </span>
                    <span className="md:hidden">Resume</span>
                  </button>
                )}
              </div>
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-100"></div>
                </div>
              ) : !subscription ? (
                <div className="text-center py-20">
                  <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500 text-lg">
                    Subscription not found
                  </p>
                </div>
              ) : (
                <div className="px-3 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
                  {/* Subscription Overview */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 shadow-sm">
                    {/* Mobile: Single row layout */}
                    <div className="flex md:hidden items-start justify-between mb-4 gap-2">
                      <h2 className="text-base font-bold text-primary-text break-words flex-1 line-clamp-2">
                        {subscription.subscription_name}
                      </h2>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-gray-900 text-base whitespace-nowrap">
                            {subscription?.formatted_total}
                          </span>
                          <div className="relative">
                            <button
                              onClick={() =>
                                setShowPriceBreakdown(!showPriceBreakdown)
                              }
                              className="text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                            {showPriceBreakdown && (
                              <div className="absolute right-0 top-6 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[250px]">
                                <h4 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                                  Price Breakdown
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">
                                      Item Price: ({subscription?.item?.quantity || 0} qty)
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {subscription?.item
                                        ?.formatted_product_price || "$0.00"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">
                                      Preference Price:
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {subscription?.item
                                        ?.formatted_preference_total || "$0.00"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">
                                      Subtotal:
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {subscription?.formatted_order_sub_total ||
                                        "$0.00"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">
                                      Shipping:
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {subscription?.formatted_shipping_total ||
                                        "$0.00"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between pt-2 border-t border-gray-200">
                                    <span className="font-semibold text-gray-900">
                                      Total:
                                    </span>
                                    <span className="font-bold text-primary-100">
                                      {subscription?.formatted_total}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${
                            subscription.status === "active"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {subscription.status}
                        </span>
                      </div>
                    </div>

                    {/* Desktop: Original layout */}
                    <div className="hidden md:flex md:flex-row md:justify-between md:items-start mb-4 gap-2">
                      <div className="flex-1">
                        <div className="flex flex-col md:flex-row md:items-baseline gap-2">
                          <h2 className="text-lg md:text-xl font-bold text-primary-text break-words">
                            {subscription.subscription_name}
                          </h2>

                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900 text-lg md:text-xl whitespace-nowrap">
                              {subscription?.formatted_total}
                            </span>
                            <div className="relative">
                              <button
                                onMouseEnter={() => setShowPriceBreakdown(true)}
                                onMouseLeave={() =>
                                  setShowPriceBreakdown(false)
                                }
                                onClick={() =>
                                  setShowPriceBreakdown(!showPriceBreakdown)
                                }
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                              >
                                <Info className="w-4 h-4" />
                              </button>

                              {showPriceBreakdown && (
                                <div className="absolute left-0 md:left-auto md:right-0 top-6 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[250px]">
                                  <h4 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                                    Price Breakdown
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">
                                        Item Price: ({subscription?.item?.quantity || 0} qty)
                                      </span>
                                      <span className="font-medium text-gray-900">
                                        {subscription?.item
                                          ?.formatted_product_price || "$0.00"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">
                                        Preference Price:
                                      </span>
                                      <span className="font-medium text-gray-900">
                                        {subscription?.item
                                          ?.formatted_preference_total ||
                                          "$0.00"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">
                                        Subtotal:
                                      </span>
                                      <span className="font-medium text-gray-900">
                                        {subscription?.formatted_order_sub_total ||
                                          "$0.00"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">
                                        Shipping:
                                      </span>
                                      <span className="font-medium text-gray-900">
                                        {subscription?.formatted_shipping_total ||
                                          "$0.00"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-gray-200">
                                      <span className="font-semibold text-gray-900">
                                        Total:
                                      </span>
                                      <span className="font-bold text-primary-100">
                                        {subscription?.formatted_total}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <span
                        className={`px-3 py-1 text-xs font-semibold rounded-full capitalize self-start ${
                          subscription.status === "active"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {subscription.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 md:gap-4 text-sm">
                      {/* Left Column */}
                      <div className="space-y-3 md:space-y-4 col-span-5 md:col-span-3">
                        <div className="flex items-start gap-2">
                          <RefreshCw className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                          <div className="flex flex-wrap items-center gap-1 flex-1">
                            <span className="text-gray-600 w-28 md:w-36">
                              Frequency:
                            </span>
                            <span className="font-medium text-gray-900 capitalize">
                              {subscription.frequency_details?.description}
                            </span>
                            <button
                              onClick={handleUpdateFrequencyClick}
                              className="text-red-600 hover:text-gray-700 transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5 -mt-2 ml-1" />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-start gap-2">
                          <CreditCard className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                          <div className="flex flex-wrap items-center gap-1 flex-1">
                            <span className="text-gray-600 w-28 md:w-36">
                              Payment:
                            </span>
                            <span className="font-medium text-gray-900 capitalize break-all">
                              {subscription.payment_info?.payment_method?.replace(
                                "-",
                                " "
                              )}{" "}
                              {subscription.payment_info?.card_number}
                            </span>
                            <button
                              onClick={handlePaymentClick}
                              className="text-red-600 hover:text-gray-700 transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5 -mt-2 ml-1" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="space-y-3 md:space-y-4 col-span-5 md:col-span-2">
                        <div className="flex items-start gap-2">
                          <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="text-gray-600 w-28 md:w-36">
                              Next Delivery:
                            </span>
                            <span className="font-medium text-gray-900">
                              {new Date(
                                subscription.next_delivery_date
                              ).toLocaleDateString("en-US", {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-start gap-2">
                          <Truck className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                          <div className="flex flex-wrap items-center gap-1 flex-1">
                            <span className="text-gray-600 w-28 md:w-36">
                              Delivery Method:
                            </span>
                            <span className="font-medium text-gray-900 capitalize">
                              {subscription.delivery_method}
                            </span>
                            <button
                              onClick={() => setShowDeliveryMethodModal(true)}
                              className="text-red-600 hover:text-gray-700 transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5 -mt-2 ml-1" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Full Width Items */}
                      {subscription?.delivery_address && (
                        <div className="md:col-span-5 flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                          <div className="flex flex-wrap gap-1">
                            <span className="text-gray-600 w-28 md:w-36">
                              Delivery Address:
                            </span>
                            <div className="text-sm break-words flex-1">
                              {subscription.delivery_address.name && (
                                <span className="text-gray-900 font-semibold capitalize">
                                  {subscription.delivery_address.name}
                                </span>
                              )}
                              {subscription.delivery_address.address && (
                                <span className="text-gray-700">
                                  {subscription.delivery_address.name && ", "}
                                  {subscription.delivery_address.address}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Products in Subscription */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3 md:p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-gray-700" />
                        <h3 className="text-lg font-bold text-primary-text">
                          Product in this Subscription
                        </h3>
                      </div>
                      {subscription.status !== "cancelled" && !isEditingProduct && (
                        <button
                          onClick={handleEditProductClick}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit
                        </button>
                      )}
                    </div>
                    {subscription.item && (
                      <div className="space-y-3">
                        {/* First Row: Image, Title, Price */}
                        <div className="flex items-start gap-3 md:gap-4">
                          {/* Product Image */}
                          <div className="w-20 h-20 md:w-24 md:h-24 bg-gray-200 rounded-md overflow-hidden flex-shrink-0">
                            <img
                              src={
                                subscription.item.product_image || noproducts
                              }
                              alt={subscription.item.product_name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = noproducts;
                              }}
                            />
                          </div>

                          {/* Product Title and Price */}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-base md:text-lg font-semibold text-black mb-1 line-clamp-2">
                              {subscription.item.product_name}
                            </h4>
                            <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm md:text-base text-gray-600">
                              {isEditingProduct ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-600">Qty:</span>
                                  <div className="flex items-center border border-gray-300 rounded-md">
                                    <button
                                      onClick={() => setEditQuantity(prev => Math.max(1, prev - 1))}
                                      className="p-1.5 hover:bg-gray-100 transition-colors rounded-l-md"
                                      disabled={editQuantity <= 1}
                                    >
                                      <Minus className="w-4 h-4 text-gray-600" />
                                    </button>
                                    <span className="px-3 py-1 min-w-[40px] text-center font-semibold text-gray-900 border-x border-gray-300">
                                      {editQuantity}
                                    </span>
                                    <button
                                      onClick={() => setEditQuantity(prev => prev + 1)}
                                      className="p-1.5 hover:bg-gray-100 transition-colors rounded-r-md"
                                    >
                                      <Plus className="w-4 h-4 text-gray-600" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <span>Qty: {subscription.item.quantity}</span>
                              )}
                              <span className="font-semibold text-red-700">
                                {subscription?.item?.formatted_product_total}
                              </span>
                            </div>
                            {isEditingProduct && (
                              <div className="flex items-center gap-2 mt-3">
                                <button
                                  onClick={handleUpdateQuantity}
                                  disabled={isSubmitting || editQuantity === subscription.item.quantity}
                                  className={`px-4 py-2 text-sm font-semibold text-white bg-red-gradient rounded-md transition-all ${
                                    isSubmitting || editQuantity === subscription.item.quantity
                                      ? "opacity-50 cursor-not-allowed"
                                      : "hover:shadow-md"
                                  }`}
                                >
                                  {isSubmitting ? (
                                    <div className="flex items-center gap-2">
                                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      Updating...
                                    </div>
                                  ) : (
                                    "Update Quantity"
                                  )}
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  disabled={isSubmitting}
                                  className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Second Row: Preferences, Cut, Notes - Full Width */}
                        {subscription.item.preferences && (
                          <div className="w-full pt-3 border-t border-gray-200">
                            {/* Preference Items */}
                            {subscription.item.preferences.preference &&
                              subscription.item.preferences.preference.length >
                                0 && (
                                <div className="text-sm mb-2">
                                  <span className="font-medium text-gray-700">
                                    Preferences:{" "}
                                  </span>
                                  {subscription.item.preferences.preference.map(
                                    (pref, idx) => (
                                      <span key={idx} className="text-gray-600">
                                        {pref.label} (
                                        <span className="font-semibold text-gray-900">
                                          ${parseFloat(pref.price).toFixed(2)}
                                        </span>
                                        )
                                        {idx <
                                          subscription.item.preferences
                                            .preference.length -
                                            1 && ", "}
                                      </span>
                                    )
                                  )}
                                </div>
                              )}

                            {/* Cut Preference */}
                            {subscription.item.preferences.cut && (
                              <div className="text-sm mb-2">
                                <span className="font-medium text-gray-700">
                                  Cut:{" "}
                                </span>
                                <span className="text-gray-600">
                                  {subscription.item.preferences.cut.label} (
                                  <span className="font-semibold text-gray-900">
                                    $
                                    {parseFloat(
                                      subscription.item.preferences.cut.price
                                    ).toFixed(2)}
                                  </span>
                                  )
                                </span>
                              </div>
                            )}

                            {/* Custom Note */}
                            {subscription.item.preferences.custom_note && (
                              <div className="text-sm">
                                <span className="font-medium text-gray-700">
                                  Custom Notes:{" "}
                                </span>
                                <span className="text-gray-600">
                                  {subscription.item.preferences.custom_note}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Upcoming Deliveries */}
                  {subscription.schedules.length > 0 && subscription.status !== "cancelled" && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm relative">
                      {/* Overlay when paused */}
                      {subscription?.status === "paused" && (
                        <div className="absolute inset-0 bg-gray-500/40 backdrop-blur-[1px] rounded-lg z-10 flex items-center justify-center">
                          <div className="bg-white rounded-lg p-6 shadow-xl text-center max-w-sm mx-4">
                            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Pause className="w-8 h-8 text-orange-600" />
                            </div>
                            <h4 className="text-lg font-bold text-gray-900 mb-2">
                              Subscription Paused
                            </h4>
                            <p className="text-sm text-gray-600 mb-4">
                              This subscription is currently paused. Resume to
                              manage deliveries.
                            </p>
                            <div className="flex justify-center">
                              <button
                                onClick={handleResumeClick}
                                disabled={isSubmitting}
                                className="w-4/6 px-4 py-2.5 border-2 border-green-500 text-green-600 hover:bg-green-50 font-semibold rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                              >
                                <Play size={16} />
                                <span>Resume Subscription</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Original content */}
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-gray-700" />
                          <h3 className="text-lg font-bold text-primary-text">
                            Upcoming Deliveries
                          </h3>
                        </div>
                        <span className="text-sm text-gray-600">
                          {subscription.schedules?.length || 0} total
                        </span>
                      </div>

                      <div className="space-y-3">
                        {subscription.schedules
                          ?.slice(
                            0,
                            showAllSchedules ? subscription.schedules.length : 5
                          )
                          .map((schedule, index) => (
                            <div
                              key={schedule.id}
                              className="border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center justify-between p-4">
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-2 h-2 rounded-full ${
                                      schedule.status === "scheduled"
                                        ? "bg-blue-500"
                                        : schedule.status === "completed"
                                        ? "bg-green-500"
                                        : schedule.status === "skipped"
                                        ? "bg-orange-500"
                                        : "bg-gray-500"
                                    }`}
                                  ></div>
                                  <span className="font-medium text-gray-900">
                                    {new Date(
                                      schedule.scheduled_date
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    })}
                                  </span>
                                  <span
                                    className={`px-2.5 py-0.5 text-xs font-medium rounded-full capitalize ${
                                      schedule.status === "scheduled"
                                        ? "bg-blue-50 text-blue-700"
                                        : schedule.status === "completed"
                                        ? "bg-green-50 text-green-700"
                                        : schedule.status === "skipped"
                                        ? "bg-orange-50 text-orange-700"
                                        : "bg-gray-50 text-gray-700"
                                    }`}
                                  >
                                    {schedule.status}
                                  </span>
                                </div>
                                {schedule.status === "scheduled" && (
                                  <button
                                    onClick={() => handleSkipClick(schedule)}
                                    disabled={isSubmitting}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium rounded-md transition-colors flex items-center gap-1 disabled:opacity-50"
                                  >
                                    Skip <ChevronRight className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                              {schedule.status === "skipped" &&
                                schedule.skip_reason && (
                                  <div className="px-4 pb-4 pt-0">
                                    <div className="bg-orange-50 border-l-4 border-orange-400 rounded p-3">
                                      <div className="flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                                        <div>
                                          <p className="text-xs font-medium text-orange-800 mb-1">
                                            Skip Reason
                                          </p>
                                          <p className="text-sm text-orange-700">
                                            {schedule.skip_reason}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                            </div>
                          ))}
                      </div>

                      {subscription.schedules &&
                        subscription.schedules.length > 5 && (
                          <div className="mt-4 text-center">
                            <button
                              onClick={() =>
                                setShowAllSchedules(!showAllSchedules)
                              }
                              className="text-primary-100 hover:text-primary-100/80 font-medium text-sm flex items-center justify-center gap-1 mx-auto transition-colors"
                            >
                              {showAllSchedules ? (
                                <>
                                  View Less <ChevronUp className="w-4 h-4" />
                                </>
                              ) : (
                                <>
                                  View More ({subscription.schedules.length - 5}{" "}
                                  more) <ChevronDown className="w-4 h-4" />
                                </>
                              )}
                            </button>
                          </div>
                        )}
                    </div>
                  )}

                  {/* Manage Subscription */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-primary-text mb-4">
                      Manage Subscription
                    </h3>
                    {subscription.status === "cancelled" ? (
                      <div className="flex items-center gap-2 text-red-600 font-semibold py-3">
                        <Ban className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm md:text-base">
                          This subscription was cancelled
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="flex gap-3 overflow-x-auto">
                          {subscription.status === "paused" ? (
                            <button
                              onClick={handleResumeClick}
                              disabled={isSubmitting}
                              className="px-3 md:px-6 py-2 border-2 border-green-500 text-green-600 hover:bg-green-50 font-semibold rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm md:text-base whitespace-nowrap"
                            >
                              <Play size={16} />
                              <span className="hidden md:inline">
                                Resume Subscription
                              </span>
                              <span className="md:hidden">Resume</span>
                            </button>
                          ) : (
                            <button
                              onClick={handlePauseClick}
                              disabled={isSubmitting}
                              className="px-3 md:px-6 py-2 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm md:text-base whitespace-nowrap"
                            >
                              <Pause size={16} />
                              <span className="hidden md:inline">
                                Pause Subscription
                              </span>
                              <span className="md:hidden">Pause</span>
                            </button>
                          )}
                          <button
                            onClick={handleCancelClick}
                            disabled={isSubmitting}
                            className="px-3 md:px-6 py-2 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm md:text-base whitespace-nowrap"
                          >
                            <X size={16} />
                            <span className="hidden md:inline">
                              Cancel Subscription
                            </span>
                            <span className="md:hidden">Cancel</span>
                          </button>
                        </div>

                        {subscription.status === "paused" && (
                          <p className="text-sm text-gray-500 mt-2">
                            Your order will be delivered on{" "}
                            {new Date(
                              subscription.next_delivery_date
                            ).toLocaleDateString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Change History */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <FileText className="w-5 h-5 text-gray-700" />
                      <h3 className="text-lg font-bold text-primary-text">
                        Change History
                      </h3>
                    </div>

                    {subscription?.modifications &&
                    subscription.modifications.length > 0 ? (
                      <div className="max-h-96 overflow-y-auto pr-2">
                        <div className="relative">
                          {subscription.modifications.map(
                            (modification, index) => {
                              const isLast =
                                index === subscription.modifications.length - 1;
                              const title = getModificationTitle(
                                modification.modification_type
                              );

                              return (
                                <div
                                  key={modification.id}
                                  className="relative flex gap-4 pb-6 last:pb-0"
                                >
                                  <div
                                    className="relative flex flex-col items-center"
                                    style={{ width: "16px" }}
                                  >
                                    <div className="w-3 h-3 rounded-full bg-primary-100 ring-4 ring-white border-2 border-primary-100 z-10 mt-1"></div>
                                    {!isLast && (
                                      <div
                                        className="absolute w-0.5 bg-gray-300"
                                        style={{
                                          top: "16px",
                                          bottom: "-24px",
                                          left: "7px",
                                        }}
                                      ></div>
                                    )}
                                  </div>

                                  {/* Content */}
                                  <div className="flex-1 pr-4">
                                    <div className="flex justify-between items-start mb-2">
                                      <h4 className="font-semibold text-gray-900">
                                        {title}
                                      </h4>
                                      <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                                        {new Date(
                                          modification.created_at
                                        ).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </span>
                                    </div>

                                    {/* Reason */}
                                    {modification.reason && (
                                      <p className="text-sm text-gray-700 mb-2">
                                        <span className="font-medium">
                                          Reason:
                                        </span>{" "}
                                        {modification.reason}
                                      </p>
                                    )}

                                    {/* Changes Details */}
                                    {modification.old_value &&
                                      modification.new_value && (
                                        <div className="text-sm text-gray-600 space-y-1">
                                          {Object.keys(modification.new_value)
                                            .filter(
                                              (key) =>
                                                key !== "payment_id" &&
                                                key !== "pickup_id" &&
                                                modification.new_value[key] !==
                                                  null &&
                                                modification.new_value[key] !==
                                                  undefined &&
                                                modification.new_value[key] !==
                                                  ""
                                            )
                                            .map((key) => (
                                              <div
                                                key={key}
                                                className="flex gap-2 flex-wrap"
                                              >
                                                <span className="font-medium capitalize">
                                                  {key.replace(/_/g, " ")}:
                                                </span>
                                                <span className="line-through text-gray-400">
                                                  {String(
                                                    modification.old_value[key]
                                                  )}
                                                </span>
                                                <span>→</span>
                                                <span className="text-primary-100 font-medium">
                                                  {String(
                                                    modification.new_value[key]
                                                  )}
                                                </span>
                                              </div>
                                            ))}
                                        </div>
                                      )}

                                    {/* For created type, show initial values */}
                                    {modification.modification_type ===
                                      "created" &&
                                      modification.new_value && (
                                        <div className="text-sm text-gray-600 space-y-1">
                                          {Object.keys(modification.new_value)
                                            .filter(
                                              (key) =>
                                                modification.new_value[key] !==
                                                  null &&
                                                modification.new_value[key] !==
                                                  undefined &&
                                                modification.new_value[key] !==
                                                  ""
                                            )
                                            .map((key) => (
                                              <div
                                                key={key}
                                                className="flex gap-2 flex-wrap"
                                              >
                                                <span className="font-medium capitalize">
                                                  {key.replace(/_/g, " ")}:
                                                </span>
                                                <span className="text-primary-100 font-medium">
                                                  {String(
                                                    modification.new_value[key]
                                                  )}
                                                </span>
                                              </div>
                                            ))}
                                        </div>
                                      )}
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-600 text-center py-8">
                        No change history available
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
      </div>

      {/* Pause Confirmation Modal */}
      <PauseSubscriptionModal
        isOpen={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        onConfirm={confirmPause}
        subscriptionName={subscription?.subscription_name}
        schedules={subscription?.schedules}
        isSubmitting={isSubmitting}
      />

      {/* Resume Confirmation Modal */}
      <ResumeSubscriptionModal
        isOpen={showResumeModal}
        onClose={() => setShowResumeModal(false)}
        onConfirm={confirmResume}
        subscriptionName={subscription?.subscription_name}
        schedules={subscription?.schedules}
        isSubmitting={isSubmitting}
      />

      {/* Cancel Confirmation Modal */}
      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancel}
        subscriptionName={subscription?.subscription_name}
        isSubmitting={isSubmitting}
      />

      {/* Skip Delivery Modal */}
      <SkipDeliveryModal
        isOpen={showSkipModal}
        onClose={() => {
          setShowSkipModal(false);
          setSelectedSchedule(null);
        }}
        onConfirm={confirmSkip}
        deliveryDate={
          selectedSchedule
            ? new Date(selectedSchedule.scheduled_date).toLocaleDateString(
                "en-US",
                {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                }
              )
            : null
        }
        isSubmitting={isSubmitting}
      />

      {/* Delivery Method Modal */}
      <Dialog
        open={showDeliveryMethodModal}
        onClose={handleCloseDeliveryModal}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <DialogPanel className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="flex items-center justify-between bg-red-gradient p-4 rounded-t-lg">
              <DialogTitle className="text-lg font-semibold letter-spacing-1 text-white">
                Update Delivery Method
              </DialogTitle>
              <button
                onClick={handleCloseDeliveryModal}
                className="text-white hover:text-gray-200 transition-colors"
                disabled={isSubmitting}
              >
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[80vh]">
              <div className="p-5">
                <DeliveryMethodFields
                  onAddressSelect={handleAddressSelect}
                  bottomSheet={false}
                  deliveryMethods={coreSettings?.DELIVERY_METHOD || []}
                  skipApiCall={true}
                  initialDeliveryMethod={subscription?.delivery_method}
                  initialPickupId={subscription?.delivery_address?.pickup_id}
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t">
              <div className="text-center">
                <button
                  onClick={handleUpdateDeliveryMethod}
                  disabled={isSubmitting}
                  className={`main-btn py-3 px-10 w-fit letter-spacing-07 font-semibold ${
                    isSubmitting
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:shadow-lg"
                  } transition-all duration-200`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Updating...
                    </div>
                  ) : (
                    "Update Delivery Method"
                  )}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Update Frequency Modal */}
      <SubscriptionModal
        open={showFrequencyModal}
        setOpen={setShowFrequencyModal}
        product={subscription?.item}
        onSubscribe={confirmUpdateFrequency}
        isUpdateMode={true}
        subscriptionData={subscription}
      />

      {/* Payment Details Modal */}
      <SubscriptionPaymentModal
        open={showPaymentModal}
        setOpen={setShowPaymentModal}
        subscriptionId={subscription?.id}
        onUpdate={loadSubscriptionDetails}
        paymentCardId={subscription?.payment_info?.payment_id}
      />
    </div>
  );
};

export default AdminSubscriptionDetailsPage;
